/**
 * Customer Portal Tickets List Page
 * View all support tickets for the customer
 */

import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import CustomerPortalLayout from '@/components/customer-portal/CustomerPortalLayout';
import { customerPortalService, type PortalTicket } from '@/services/api/customerPortal';
import {
  Ticket,
  Plus,
  Search,
  Clock,
  MessageSquare,
  Filter,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
} from 'lucide-react';

export default function CustomerPortalTicketsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tickets, setTickets] = useState<PortalTicket[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    loadTickets();
  }, [statusFilter, page]);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const params: any = {
        limit: pageSize,
        offset: (page - 1) * pageSize,
      };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response = await customerPortalService.getMyTickets(params);
      setTickets(response.tickets);
      setTotal(response.total);
    } catch (error) {
      console.error('Failed to load tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setPage(1);
    if (value === 'all') {
      searchParams.delete('status');
    } else {
      searchParams.set('status', value);
    }
    setSearchParams(searchParams);
  };

  const getStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    if (['new', 'open'].includes(statusLower)) {
      return <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">Open</Badge>;
    }
    if (['pending', 'waiting'].includes(statusLower)) {
      return <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">Pending</Badge>;
    }
    if (['in_progress'].includes(statusLower)) {
      return <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">In Progress</Badge>;
    }
    if (['resolved', 'closed'].includes(statusLower)) {
      return <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">Resolved</Badge>;
    }
    return <Badge variant="outline">{status}</Badge>;
  };

  const getPriorityBadge = (priority: string) => {
    const priorityLower = priority.toLowerCase();
    if (priorityLower === 'urgent') {
      return <Badge variant="destructive">Urgent</Badge>;
    }
    if (priorityLower === 'high') {
      return <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">High</Badge>;
    }
    if (priorityLower === 'medium') {
      return <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">Medium</Badge>;
    }
    return <Badge variant="outline" className="text-gray-500">Low</Badge>;
  };

  const filteredTickets = tickets.filter(ticket =>
    searchQuery === '' ||
    ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ticket.ticketNumber.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(total / pageSize);

  return (
    <CustomerPortalLayout title="My Tickets">
      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          {/* Search */}
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search tickets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Status Filter */}
          <Select value={statusFilter} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-full sm:w-40">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tickets</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Link to="/customer-portal/tickets/new">
          <Button className="bg-v2accent hover:bg-v2accent/90 w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-2" />
            New Ticket
          </Button>
        </Link>
      </div>

      {/* Tickets List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8">
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="animate-pulse p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-2" />
                    <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                  </div>
                ))}
              </div>
            </div>
          ) : filteredTickets.length > 0 ? (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredTickets.map((ticket) => (
                <Link
                  key={ticket.id}
                  to={`/customer-portal/tickets/${ticket.id}`}
                  className="block p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-mono text-gray-500">#{ticket.ticketNumber}</span>
                      {getStatusBadge(ticket.status)}
                      {getPriorityBadge(ticket.priority)}
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <Clock className="w-3 h-3 mr-1" />
                      {new Date(ticket.createdAt).toLocaleDateString()}
                    </div>
                  </div>

                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                    {ticket.subject}
                  </h3>

                  {ticket.description && (
                    <p className="text-sm text-gray-500 line-clamp-2 mb-2">
                      {ticket.description}
                    </p>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      {ticket.categoryName && (
                        <span className="flex items-center">
                          <Ticket className="w-3 h-3 mr-1" />
                          {ticket.categoryName}
                        </span>
                      )}
                      {ticket.comments.length > 0 && (
                        <span className="flex items-center">
                          <MessageSquare className="w-3 h-3 mr-1" />
                          {ticket.comments.length} {ticket.comments.length === 1 ? 'reply' : 'replies'}
                        </span>
                      )}
                    </div>
                    {ticket.lastResponseAt && (
                      <span className="text-xs text-gray-400">
                        Last response: {new Date(ticket.lastResponseAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Ticket className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {searchQuery || statusFilter !== 'all' ? 'No tickets found' : 'No tickets yet'}
              </h3>
              <p className="text-gray-500 mb-4">
                {searchQuery || statusFilter !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'Create your first support ticket to get help'}
              </p>
              {!searchQuery && statusFilter === 'all' && (
                <Link to="/customer-portal/tickets/new">
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Ticket
                  </Button>
                </Link>
              )}
            </div>
          )}
        </CardContent>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500">
              Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, total)} of {total} tickets
            </p>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-gray-500">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </CustomerPortalLayout>
  );
}
